// servizi/contatti.ts — v046: «Invita dalla rubrica», come WhatsApp (l'interfaccia è componenti/InvitaRubrica.tsx).
// Nelle app Android/iOS: il plugin @capacitor-community/contacts (chiede il permesso dei contatti, apre il selettore
// del sistema). Nel browser: il selettore dei contatti del browser (Contact Picker API, `navigator.contacts.select`:
// c'è su Chrome Android; su computer e iPhone dal sito no → rubricaDisponibile() è false e il pulsante non si vede).
// PRIVACY: i contatti scelti restano sul telefono. Non si mandano MAI al nostro server: servono solo per aprire
// l'app dei messaggi o WhatsApp con il messaggio già scritto. Le funzioni pure sono in numeriTelefono.ts.
import { NATIVA } from "./nativo.ts";
import { PIATTAFORMA } from "../costanti.ts";
import { linkSms, linkWa, normalizzaNumeri, regioneDaLingua, type SistemaSms } from "./numeriTelefono.ts";

export { normalizzaNumero, normalizzaNumeri } from "./numeriTelefono.ts";

export interface Contatto {
  nome: string;
  /** In forma internazionale («+39…») quando il paese si conosce; senza doppioni. */
  telefoni: string[];
}

/** Il selettore dei contatti del browser (Contact Picker API: non è nei tipi di TypeScript). */
interface SelettoreContatti {
  select(proprieta: ("name" | "tel")[], opzioni?: { multiple?: boolean }): Promise<{ name?: string[]; tel?: string[] }[]>;
  getProperties?(): Promise<string[]>;
}
const selettoreBrowser = (): SelettoreContatti | null => {
  if (typeof navigator === "undefined" || typeof window === "undefined") return null;
  const c = (navigator as Navigator & { contacts?: SelettoreContatti }).contacts;
  // la API c'è solo in un contesto sicuro (https) e dentro la pagina principale
  return c && typeof c.select === "function" && window.isSecureContext && window.top === window.self ? c : null;
};

/** Si possono scegliere persone dalla rubrica qui? (app: sempre; browser: solo con il selettore dei contatti) */
export function rubricaDisponibile(): boolean {
  return NATIVA || selettoreBrowser() !== null;
}

/** La regione del telefono (per i numeri salvati senza «+»), dalla lingua del sistema: «it-IT» → «IT». */
function regioneTelefono(): string {
  if (typeof navigator === "undefined") return "";
  for (const l of [...(navigator.languages ?? []), navigator.language]) {
    const r = regioneDaLingua(l);
    if (r) return r;
  }
  try {
    return regioneDaLingua(Intl.DateTimeFormat().resolvedOptions().locale);
  } catch {
    return "";
  }
}

/** Errore con il motivo: «permesso» (negato dall'utente) o «assente» (niente rubrica qui). */
export class ErroreRubrica extends Error {
  readonly motivo: "permesso" | "assente";
  constructor(motivo: "permesso" | "assente") {
    super(`rubrica: ${motivo}`);
    this.motivo = motivo;
  }
}

/**
 * Il plugin (7.2.0) non risponde MAI se si chiude il selettore senza scegliere (né su Android né su iOS). Su Android
 * il selettore è un'altra schermata: quando si torna all'app («resume») e dopo qualche secondo non è arrivato niente,
 * si considera annullato (null). Su iOS il selettore sta dentro l'app e non c'è modo di saperlo: la promessa resta
 * aperta (l'interfaccia non deve aspettarla con una rotella che blocca).
 */
async function conRitornoAndroid<T>(scelta: Promise<T>): Promise<T | null> {
  if (PIATTAFORMA !== "android") return scelta;
  let finito = false;
  let maniglia: { remove(): unknown } | null = null;
  const ritorno = new Promise<null>((ok) => {
    import("@capacitor/app")
      .then(({ App }) => App.addListener("resume", () => setTimeout(() => ok(null), 4000)))
      .then((h) => {
        maniglia = h;
        if (finito) void h.remove(); // la scelta è arrivata prima che l'ascolto fosse pronto
      })
      .catch(() => undefined);
  });
  try {
    return await Promise.race([scelta, ritorno]);
  } finally {
    finito = true;
    void (maniglia as { remove(): unknown } | null)?.remove();
  }
}

const annullato = (e: unknown): boolean => /cancel|annull|abort|dismiss/i.test(String((e as Error)?.message ?? e)) || (e as Error)?.name === "AbortError";

/**
 * Apre il selettore dei contatti e restituisce le persone scelte con i loro numeri (normalizzati; chi non ha numeri
 * resta fuori). Nel browser se ne scelgono più insieme; nelle app il selettore del sistema ne dà una per volta (si
 * richiama per aggiungerne altre). Annullato → []. Permesso negato o rubrica assente → ErroreRubrica.
 */
export async function scegliContatti(): Promise<Contatto[]> {
  const regione = regioneTelefono();
  if (NATIVA) {
    let plugin: typeof import("@capacitor-community/contacts");
    try {
      plugin = await import("@capacitor-community/contacts");
    } catch {
      throw new ErroreRubrica("assente"); // build dell'app senza il plugin
    }
    const { Contacts } = plugin;
    try {
      let p = await Contacts.checkPermissions();
      if (p.contacts !== "granted" && p.contacts !== "limited") p = await Contacts.requestPermissions();
      if (p.contacts !== "granted" && p.contacts !== "limited") throw new ErroreRubrica("permesso");
      const scelta = await conRitornoAndroid(Contacts.pickContact({ projection: { name: true, phones: true } }));
      if (!scelta) return [];
      const { contact } = scelta;
      const telefoni = normalizzaNumeri((contact?.phones ?? []).map((t) => t.number), regione);
      if (!telefoni.length) return [];
      const n = contact.name;
      const nome = (n?.display || [n?.given, n?.family].filter(Boolean).join(" ")).trim();
      return [{ nome, telefoni }];
    } catch (e) {
      if (e instanceof ErroreRubrica) throw e;
      if (annullato(e)) return [];
      if (/permission/i.test(String((e as Error)?.message ?? e))) throw new ErroreRubrica("permesso");
      throw e;
    }
  }
  const sel = selettoreBrowser();
  if (!sel) throw new ErroreRubrica("assente");
  try {
    const scelti = await sel.select(["name", "tel"], { multiple: true });
    return scelti.flatMap((c) => {
      const telefoni = normalizzaNumeri(c.tel ?? [], regione);
      return telefoni.length ? [{ nome: (c.name?.find((x) => x?.trim()) ?? "").trim(), telefoni }] : [];
    });
  } catch (e) {
    if (annullato(e)) return [];
    // il browser rifiuta se la pagina non è in primo piano o senza un tocco dell'utente: come «annullato»
    if ((e as Error)?.name === "InvalidStateError" || (e as Error)?.name === "SecurityError") return [];
    throw e;
  }
}

/** iOS (app o Safari su iPhone/iPad) o Android/altro: la sintassi del corpo dell'SMS cambia. */
function sistemaSms(): SistemaSms {
  if (PIATTAFORMA === "ios") return "ios";
  if (PIATTAFORMA === "android") return "android";
  if (typeof navigator === "undefined") return "android";
  const ua = navigator.userAgent;
  // iPadOS si presenta come un Mac: lo tradisce lo schermo touch
  return /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1) ? "ios" : "android";
}

/** Link «sms:» per mandare `testo` a questi numeri (con la sintassi giusta per iOS o Android). */
export function linkInvitoSms(numeri: string[], testo: string): string {
  return linkSms(numeri, testo, sistemaSms());
}

/** Link per aprire WhatsApp sulla chat di `numero` con `testo` già scritto. */
export function linkWhatsapp(numero: string, testo: string): string {
  return linkWa(numero, testo);
}
